import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import {
  getEmergencyContactFromIntakeForm,
  IntakeEmergencyContact,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'

import { getAuthUserId } from '../../../data/auth/authSelectors'
import { useGetAssignmentsQuery } from '../../../lyraTherapy/assignments/data/assignmentsApi'
import { useGetClientQuery } from '../../../lyraTherapy/clients/clientDetails/data/ltClientApi'

export type RiskAlertPopoverData = {
  clientFirstName?: string
  clientPhone?: string
  voicemailConsent?: boolean
  emergencyContact: IntakeEmergencyContact
  isLoading: boolean
}

/**
 * Hook that fetches client phone, emergency contact for the risk alert popover.
 * Popover open/close state is managed by the panel (via #17995's openPopoverId).
 */
export const useRiskAlertPopoverData = (
  clientId: string | null,
): {
  data: RiskAlertPopoverData
} => {
  const providerId = useSelector(getAuthUserId)

  const { data: clientData, isFetching: isFetchingClient } = useGetClientQuery(
    clientId ? { providerId, clientId } : skipToken,
  )

  const { data: assignments, isFetching: isFetchingAssignments } = useGetAssignmentsQuery(
    clientId ? { providerId, patientId: clientId } : skipToken,
  )

  const emergencyContact = getEmergencyContactFromIntakeForm(assignments)

  return {
    data: {
      clientFirstName: clientData?.preferred_first_name || clientData?.first_name,
      clientPhone: clientData?.phone,
      voicemailConsent: clientData?.voicemail_consent,
      emergencyContact,
      isLoading: isFetchingClient || isFetchingAssignments,
    },
  }
}
